export type AutocompleteSigil = '@' | '#' | ':' | '+:' | '/' | '!';

export interface Suggestion {
  id: string;
  insert: string;
  label: string;
  detail?: string | null;
  avatarUrl?: string | null;
  imageUrl?: string | null;
}

export interface AutocompleteQuery {
  sigil: AutocompleteSigil;
  /** Text between the sigil and the caret; for `!`, the whole command. */
  query: string;
  /** Index of the sigil in the draft; for `!`, of the word being typed. */
  start: number;
  /** Caret position, i.e. the end of the replaceable range. */
  end: number;
}

const sigils: [AutocompleteSigil, number][] = [
  ['/', 0],
  ['@', 1],
  ['#', 1],
  [':', 2],
];
const maxQueryLength = 32;

/**
 * A sigil opens a query only at the start of the draft or after whitespace.
 */
export function activeQuery(draft: string, caret: number): AutocompleteQuery | null {
  const upToCaret = draft.slice(0, caret);
  let active: AutocompleteQuery | null = null;

  const reaction = /^\s*\+:([^:\s]*)$/.exec(upToCaret);
  if (reaction && reaction[1].length <= maxQueryLength && draft.slice(caret).trim() === '') {
    return { sigil: '+:', query: reaction[1], start: upToCaret.indexOf('+:'), end: caret };
  }

  const admin = /^\\?!([a-z]*)((?: [a-z0-9-]*)*)$/.exec(upToCaret);
  if (admin && (admin[2] === '' ? 'admin'.startsWith(admin[1]) : admin[1] === 'admin')) {
    const start = upToCaret.lastIndexOf(' ') + 1;
    return { sigil: '!', query: upToCaret, start, end: caret };
  }

  for (const [sigil, minQueryLength] of sigils) {
    const start = upToCaret.lastIndexOf(sigil);
    if (start === -1) continue;
    if (sigil === '/' && (start !== 0 || !/^[a-z0-9_-]*$/i.test(upToCaret.slice(start + 1)))) {
      continue;
    }

    const before = start === 0 ? '' : upToCaret[start - 1];
    if (before !== '' && !/\s/.test(before)) continue;

    const query = upToCaret.slice(start + 1);
    if (query.trim().length < minQueryLength || query.length > maxQueryLength) continue;
    const whitespace = sigil === '#' ? /[\r\n]/ : /\s/;
    if (whitespace.test(query) || (sigil !== '#' && query.includes(':'))) continue;

    if (!active || start > active.start) active = { sigil, query, start, end: caret };
  }

  return active;
}

export function replaceQuery(draft: string, query: AutocompleteQuery, insert: string): string {
  return `${draft.slice(0, query.start)}${insert}${draft.slice(query.end)}`;
}
